import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { WifiOff, RefreshCw, CloudUpload, AlertTriangle, Lock, Trash2, X } from 'lucide-react';
import { useOffline } from '@/contexts/OfflineContext';
import { listFailed, listHeld, heldReason, retryEntry, discardEntry } from '@/lib/sync-queue';
import { isDesktopApp } from '@/lib/desktop';
import { t } from '@/lib/i18n';
import type { SyncQueueEntry } from '@/lib/offline-store';

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleString('fr-FR');
}

function FailedSyncReview({ onClose }: { onClose: () => void }) {
  const [entries, setEntries] = useState<SyncQueueEntry[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = () => listFailed().then(setEntries);

  useEffect(() => {
    void listFailed().then(setEntries);
  }, []);

  const handleRetry = async (id: string) => {
    setBusyId(id);
    try {
      await retryEntry(id);
    } finally {
      setBusyId(null);
      void reload();
    }
  };

  const handleDiscard = async (id: string) => {
    setBusyId(id);
    try {
      await discardEntry(id);
    } finally {
      setBusyId(null);
      void reload();
    }
  };

  return (
    <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-[70] w-[min(40rem,calc(100vw-2rem))] bg-white text-gray-900 rounded-lg shadow-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          Modifications non synchronisées
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="p-1 rounded hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {entries.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            Aucune modification en échec
          </p>
        )}
        {entries.map((entry) => (
          <div key={entry.id} className="px-4 py-3 text-sm flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs text-gray-600 truncate">
                {entry.method} {entry.path}
              </p>
              <p className="text-red-700 mt-0.5">
                {entry.errorMessage ?? 'Erreur inconnue'}
                {entry.errorStatus ? ` (HTTP ${entry.errorStatus})` : ''}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{formatDate(entry.timestamp)}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => handleRetry(entry.id)}
                disabled={busyId === entry.id}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${busyId === entry.id ? 'animate-spin' : ''}`} />
                Réessayer
              </button>
              <button
                type="button"
                onClick={() => handleDiscard(entry.id)}
                disabled={busyId === entry.id}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" />
                Ignorer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Modifications withheld from the CURRENT session — queued under another
 * tenant or user, or unattributable. Deliberately NO « send anyway »
 * action: the only way out for another tenant's or user's entry is to work
 * as that user under that tenant (it then leaves on its own), and an
 * unattributable entry has no tenant or user anyone can vouch for.
 */
function HeldSyncReview({ onClose }: { onClose: () => void }) {
  const [entries, setEntries] = useState<SyncQueueEntry[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void listHeld().then(setEntries);
  }, []);

  const handleDiscard = async (id: string) => {
    setBusyId(id);
    try {
      await discardEntry(id);
    } finally {
      setBusyId(null);
      void listHeld().then(setEntries);
    }
  };

  return (
    <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-[70] w-[min(40rem,calc(100vw-2rem))] bg-white text-gray-900 rounded-lg shadow-xl border border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-600" />
          Modifications retenues
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="p-1 rounded hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="px-4 py-2 text-xs text-gray-600 border-b border-gray-100">
        {t('offline.held.intro')}
      </p>
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
        {entries.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            Aucune modification retenue
          </p>
        )}
        {entries.map((entry) => (
          <div key={entry.id} className="px-4 py-3 text-sm flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs text-gray-600 truncate">
                {entry.method} {entry.path}
              </p>
              <p className="text-slate-700 mt-0.5">{t(heldReason(entry))}</p>
              <p className="text-xs text-gray-400 mt-0.5">{formatDate(entry.timestamp)}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => handleDiscard(entry.id)}
                disabled={busyId === entry.id}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" />
                Ignorer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Layout shared by the top banners: they cover the status bar area, so they
 * carry the top and side safe-area insets the header carries without them.
 */
const TOP_BANNER_LAYOUT =
  'w-full text-white text-center text-sm font-medium flex items-center justify-center gap-2 ' +
  'pb-1.5 pt-[calc(0.375rem+env(safe-area-inset-top))] ' +
  'pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]';

/**
 * Pins a banner to the top of the viewport and reports its rendered height
 * (0 once it unmounts), which the layout uses to place the header below it:
 * on a narrow screen a banner wraps to several lines.
 */
function TopBanner({
  onHeightChange,
  children,
}: {
  onHeightChange: ((height: number) => void) | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.offsetHeight);
    report();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(report);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange]);

  return (
    <div ref={ref} className="fixed top-0 left-0 right-0 z-[60]">
      {children}
    </div>
  );
}

interface NetworkStatusBannerProps {
  /** Receives the height of the banner pinned to the top, 0 when there is none. */
  onTopHeightChange?: (height: number) => void;
}

export function NetworkStatusBanner({ onTopHeightChange }: NetworkStatusBannerProps = {}) {
  const { isOffline, isNetworkOnline, isSyncing, pendingCount, failedCount, heldCount } =
    useOffline();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [heldOpen, setHeldOpen] = useState(false);

  // Desktop : réseau coupé mais API locale joignable — l'app fonctionne
  // normalement (les données viennent de ce poste), on INFORME sans
  // alarmer ni rien mettre en pause.
  const desktopNetworkDown = isDesktopApp() && !isNetworkOnline && !isOffline;

  // Close the review panels once nothing is left to review
  useEffect(() => {
    if (failedCount === 0) setReviewOpen(false);
  }, [failedCount]);
  useEffect(() => {
    if (heldCount === 0) setHeldOpen(false);
  }, [heldCount]);

  if (
    !isOffline &&
    !desktopNetworkDown &&
    !isSyncing &&
    pendingCount === 0 &&
    failedCount === 0 &&
    heldCount === 0
  )
    return null;

  if (isOffline) {
    return (
      <TopBanner onHeightChange={onTopHeightChange}>
        <div className={`${TOP_BANNER_LAYOUT} bg-amber-500`}>
          <WifiOff className="w-4 h-4 shrink-0" />
          Mode hors ligne — Les modifications seront synchronisées automatiquement
        </div>
      </TopBanner>
    );
  }

  if (desktopNetworkDown) {
    // Pastille flottante discrète (pas un bandeau pleine largeur) : l'app
    // reste 100 % fonctionnelle en local — on informe sans recouvrir
    // l'en-tête ni décaler la mise en page.
    return (
      <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[60] bg-slate-800/95 text-white text-sm font-medium rounded-full px-4 py-2 shadow-lg flex items-center gap-2 pointer-events-none">
        <WifiOff className="w-4 h-4 text-amber-400" />
        Réseau indisponible — l'application fonctionne en local
      </div>
    );
  }

  if (isSyncing) {
    return (
      <TopBanner onHeightChange={onTopHeightChange}>
        <div className={`${TOP_BANNER_LAYOUT} bg-blue-500`}>
          <RefreshCw className="w-4 h-4 shrink-0 animate-spin" />
          Synchronisation en cours...
          {pendingCount > 0 && <span>({pendingCount} en attente)</span>}
        </div>
      </TopBanner>
    );
  }

  if (failedCount > 0) {
    return (
      <TopBanner onHeightChange={onTopHeightChange}>
        <button
          type="button"
          onClick={() => setReviewOpen((open) => !open)}
          className={`${TOP_BANNER_LAYOUT} bg-red-600 cursor-pointer hover:bg-red-700`}
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {failedCount} modification{failedCount > 1 ? 's' : ''} non synchronisée
          {failedCount > 1 ? 's' : ''} — cliquer pour examiner
        </button>
        {reviewOpen && <FailedSyncReview onClose={() => setReviewOpen(false)} />}
      </TopBanner>
    );
  }

  if (heldCount > 0) {
    return (
      <TopBanner onHeightChange={onTopHeightChange}>
        <button
          type="button"
          onClick={() => setHeldOpen((open) => !open)}
          className={`${TOP_BANNER_LAYOUT} bg-slate-700 cursor-pointer hover:bg-slate-800`}
        >
          <Lock className="w-4 h-4 shrink-0" />
          {heldCount} modification{heldCount > 1 ? 's' : ''} retenue
          {heldCount > 1 ? 's' : ''} — {t('offline.held.banner.owner')} — cliquer pour
          examiner
        </button>
        {heldOpen && <HeldSyncReview onClose={() => setHeldOpen(false)} />}
      </TopBanner>
    );
  }

  if (pendingCount > 0) {
    return (
      <TopBanner onHeightChange={onTopHeightChange}>
        <div className={`${TOP_BANNER_LAYOUT} bg-amber-500`}>
          <CloudUpload className="w-4 h-4 shrink-0" />
          {pendingCount} modification{pendingCount > 1 ? 's' : ''} en attente de synchronisation
        </div>
      </TopBanner>
    );
  }

  return null;
}
